import useFeatureToggle from '@/hooks/useFeatureToggle'
import { logger } from '@/monitoring/logger'
import { FeatureToggle } from '@/resources/enums'
import { ConnectionStatus } from '@/resources/enums/websocket'
import { ActiveOrder, ActiveOrdersData } from '@/resources/types'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { initWebSocket, setIsEnabled } from './webSocketManager'
import { OrdersWebSocketContext } from './useOrdersWebSocket'

const OrdersWebSocketProvider = ({ children }: PropsWithChildren) => {
  const isWebSocketEnabled =
    useFeatureToggle({ splitName: 'seller_enable_wss' }) === FeatureToggle.ON

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.NOT_STARTED
  )
  const [orders, setOrders] = useState<ActiveOrder[]>([])
  const [hasUpdatedOrders, setHasUpdatedOrders] = useState(false)
  const [hasInitializedWebSocket, setHasInitializedWebSocket] = useState(false)

  const isConnected = useMemo(
    () => connectionStatus === ConnectionStatus.CONNECTED,
    [connectionStatus]
  )

  const isLoading = useMemo(
    () =>
      connectionStatus === ConnectionStatus.RETRYING ||
      connectionStatus === ConnectionStatus.STARTING,
    [connectionStatus]
  )

  const isActive = useMemo(
    () => isConnected && !isLoading,
    [isConnected, isLoading]
  )

  const context = useMemo(
    () => ({ orders, isConnected, isLoading, isActive, hasUpdatedOrders }),
    [orders, isConnected, isLoading, isActive, hasUpdatedOrders]
  )

  const onMessageHandler = useCallback((ordersJson: string): void => {
    try {
      const parsed = JSON.parse(ordersJson) as ActiveOrdersData
      if (parsed?.data?.activeOrders) {
        setOrders(parsed.data.activeOrders)
        setHasUpdatedOrders(true)
      }
    } catch {
      logger.warn('[WebSocket] Failed to parse received orders')
    }
  }, [])

  const onConnectionStatusChange = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status)
    if (
      status === ConnectionStatus.RETRYING ||
      status === ConnectionStatus.FAILED
    ) {
      setHasUpdatedOrders(false)
      setOrders([])
    }
  }, [])

  useEffect(() => {
    if (!isWebSocketEnabled) {
      setConnectionStatus(ConnectionStatus.NOT_STARTED)
      setHasInitializedWebSocket(false)
      setIsEnabled(false)
    }
  }, [isWebSocketEnabled])

  useEffect(() => {
    if (
      !hasInitializedWebSocket &&
      isWebSocketEnabled &&
      connectionStatus === ConnectionStatus.NOT_STARTED
    ) {
      setHasInitializedWebSocket(true)
      initWebSocket(
        isWebSocketEnabled,
        onConnectionStatusChange,
        onMessageHandler
      )
    }
  }, [
    hasInitializedWebSocket,
    onMessageHandler,
    onConnectionStatusChange,
    isWebSocketEnabled,
    connectionStatus,
  ])

  return (
    <OrdersWebSocketContext.Provider value={context}>
      {children}
    </OrdersWebSocketContext.Provider>
  )
}

export default OrdersWebSocketProvider
